/**
 * The directory rows' view of a running generation.
 *
 * The blob never knows a generation is happening (see `genBus.ts`), so the adapter's rows cannot
 * say so either — this hook writes the live state over them: `writing · artifact 05` while the
 * stream runs, per the meta rule (status word first, timestamp dropped). No progress number is
 * invented; the bar draws only what exists, which today is nothing.
 */
import { useMemo, useSyncExternalStore } from 'react';
import type { DirectoryItem } from '~/design-system/components/v1_5/Directory';
import { parseItemKey } from '../ids';
import { genRecord, genServerVersion, genVersion, subscribeGen } from './genBus';

export function useGenRows(items: DirectoryItem[]): DirectoryItem[] {
  useSyncExternalStore(subscribeGen, genVersion, genServerVersion);
  return useMemo(() => {
    return items.map((item) => {
      const parsed = parseItemKey(item.id);
      if (!parsed || parsed.kind !== 'artifacts') return item;
      const record = genRecord(parsed.id);
      if (!record || record.state === 'failed' || record.state === 'done' || record.state === 'resolving') return item;
      // The live phase line IS the second half — `writing · Setting the palette` says more than a
      // label the row already shows one line up.
      return { ...item, status: 'generating' as const, meta: `writing · ${record.phase}` };
    });
    // The version counter IS the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, genVersion()]);
}
